// CancelWithdrawModal.tsx
import React, { useState } from 'react';
import { Modal, Radio, Input, Space, Typography, Button } from 'antd';
import { RequestData } from './types';

// Add this extended interface to include the missing properties
interface ExtendedRequestData extends RequestData {
  withdrawAmount?: number;
  username?: string;
}

export interface CancelWithdrawModalProps {
  open: boolean;
  request: ExtendedRequestData | null; // Use the extended interface here
  onCancel: () => void;
  onConfirm: (requestId: string, reason: string) => Promise<void>;
  isLoading?: boolean;
}

const CANCELLATION_REASONS = [
  "El cliente no respondió",
  "El cliente canceló la solicitud",
  "Datos bancarios incorrectos",
  "Problemas de conexión con la plataforma",
  "Intentos múltiples de retiro",
  "Saldo insuficiente al momento de confirmar",
  "Otro motivo"
] as const;

type CancellationReason = typeof CANCELLATION_REASONS[number];

export const CancelWithdrawModal: React.FC<CancelWithdrawModalProps> = ({
  open,
  request,
  onCancel,
  onConfirm,
  isLoading = false
}) => {
  const [reason, setReason] = useState<CancellationReason>(CANCELLATION_REASONS[0]);
  const [customReason, setCustomReason] = useState<string>('');

  const handleConfirm = async () => {
    if (!request) return;
    
    const finalReason = reason === 'Otro motivo' 
      ? customReason 
      : reason;
      
    if (!finalReason.trim()) {
      return; // Don't proceed if custom reason is empty
    }
    
    await onConfirm(request.id, finalReason);
  };

  const handleClose = () => {
    setReason(CANCELLATION_REASONS[0]);
    setCustomReason('');
    onCancel();
  };

  // Safely handle undefined withdrawAmount
  const withdrawAmount = request?.withdrawAmount?.toFixed(2) ?? '0.00';
  const username = request?.username ?? 'N/A';

  return (
    <Modal
      title="Cancelar Solicitud de Retiro"
      open={open}
      confirmLoading={isLoading}
      onCancel={handleClose}
      footer={[
        <Button key="back" onClick={handleClose}>
          Cancelar
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          danger
          loading={isLoading}
          onClick={handleConfirm}
          disabled={reason === 'Otro motivo' && !customReason.trim()}
        >
          Confirmar Cancelación
        </Button>,
      ]}
    >
      {request && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text>
            Estás a punto de cancelar la solicitud de retiro de <strong>{username}</strong> por un monto de <strong>${withdrawAmount}</strong>.
          </Typography.Text>
          
          <Typography.Text strong style={{ marginTop: 16 }}>
            Selecciona el motivo de cancelación:
          </Typography.Text>
          
          <Radio.Group 
            value={reason} 
            onChange={(e) => setReason(e.target.value as CancellationReason)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {CANCELLATION_REASONS.map((r) => (
                <Radio key={r} value={r}>{r}</Radio>
              ))}
            </Space>
          </Radio.Group>
          
          {reason === 'Otro motivo' && (
            <Input.TextArea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Ingresa el motivo de cancelación"
              rows={3}
              style={{ marginTop: 8 }}
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default CancelWithdrawModal;